import {
  daylight as anchors,
  type DayAnchor,
  type DaylightColors,
  type NightAnchor,
} from '@shh/design-tokens';
import { mixHex } from './color';
import { dailyExtremes, sunPosition } from './sun';
import { uiText } from '../i18n';

/**
 * Daylight flow: the ambient colours follow the sun continuously.
 *
 * Two worlds, each flowing without steps:
 *  - day   (sun above −6°, civil twilight): light theme, dark text
 *  - night (sun below −6°): dark / wall-night theme, light text
 * At −6° the text colour has to switch once – a gradual change would pass through mid-grey,
 * where no text is readable. By then it is dark in the room anyway.
 *
 * Anchors are placed by sun elevation relative to the day's maximum, so the seasons come for
 * free: in December the evening light arrives around 16:00, in June around 21:00.
 */
export const TWILIGHT = -6;

export type DaylightWorld = 'day' | 'night';
export type DaylightAnchor = DayAnchor | NightAnchor;
export type DaylightStrength = 'off' | 'subtle' | 'strong';

export const strengthFactor: Record<DaylightStrength, number> = { off: 0, subtle: 0.6, strong: 1 };

/** Name of a moment of the day in the device language (UI_TEXT.daylight). */
export const anchorLabel = (anchor: DaylightAnchor): string => uiText().daylight[anchor];

export interface DaylightState {
  world: DaylightWorld;
  /** Nearest anchor, e.g. for labels ("Abend") and time-of-day content. */
  anchor: DaylightAnchor;
  /** Sun elevation in degrees. */
  elevation: number;
  rising: boolean;
  colors: DaylightColors;
}

type Key = [elevation: number, colors: DaylightColors, name: DaylightAnchor];

const KEYS: (keyof DaylightColors)[] = ['canvas', 'raised', 'sunken', 'subtle', 'glow'];

function mixColors(a: DaylightColors, b: DaylightColors, t: number): DaylightColors {
  return Object.fromEntries(KEYS.map((k) => [k, mixHex(a[k], b[k], t)])) as DaylightColors;
}

/** Walk a keyframe list ordered along the direction of travel. */
function along(keys: Key[], elevation: number): { colors: DaylightColors; anchor: DaylightAnchor } {
  for (let i = 0; i < keys.length - 1; i++) {
    const [e0, c0, n0] = keys[i]!;
    const [e1, c1, n1] = keys[i + 1]!;
    const lo = Math.min(e0, e1);
    const hi = Math.max(e0, e1);
    if (elevation >= lo && elevation <= hi) {
      const t = e1 === e0 ? 0 : (elevation - e0) / (e1 - e0);
      return { colors: mixColors(c0, c1, t), anchor: t < 0.5 ? n0 : n1 };
    }
  }
  // Outside the range (numerical edge): nearest end.
  const first = keys[0]!;
  const last = keys[keys.length - 1]!;
  const nearest = Math.abs(elevation - first[0]) < Math.abs(elevation - last[0]) ? first : last;
  return { colors: nearest[1], anchor: nearest[2] };
}

export function computeDaylight(
  date: Date,
  latitude: number,
  longitude: number,
  strength: DaylightStrength | number = 'strong',
): DaylightState {
  const { elevation, declination } = sunPosition(date, latitude, longitude);
  const rising =
    sunPosition(new Date(date.getTime() + 10 * 60_000), latitude, longitude).elevation > elevation;
  const { max, min } = dailyExtremes(latitude, declination);
  const s = typeof strength === 'number' ? strength : strengthFactor[strength];
  const d = anchors.day;
  const n = anchors.night;

  let world: DaylightWorld;
  let flow: ReturnType<typeof along>;
  if (elevation >= TWILIGHT) {
    world = 'day';
    // Polar-ish edge: keep the morning/evening anchor strictly above sunrise.
    const mid = Math.max(0.35 * max, 1);
    const top = Math.max(max, mid + 1);
    flow = along(
      rising
        ? [
            [TWILIGHT, d.dawn, 'dawn'],
            [0, d.sunrise, 'sunrise'],
            [mid, d.morning, 'morning'],
            [top, d.noon, 'noon'],
          ]
        : [
            [top, d.noon, 'noon'],
            [mid, d.evening, 'evening'],
            [0, d.sunset, 'sunset'],
            [TWILIGHT, d.dusk, 'dusk'],
          ],
      elevation,
    );
    flow.colors = mixColors(d.noon, flow.colors, s);
  } else {
    world = 'night';
    const bottom = Math.min(min, TWILIGHT - 1);
    flow = along(
      rising
        ? [
            [bottom, n.deep, 'deep'],
            [TWILIGHT, n.predawn, 'predawn'],
          ]
        : [
            [TWILIGHT, n.duskDark, 'duskDark'],
            [bottom, n.deep, 'deep'],
          ],
      elevation,
    );
    flow.colors = mixColors(n.deep, flow.colors, s);
  }
  return { world, anchor: flow.anchor, elevation, rising, colors: flow.colors };
}

/** CSS custom properties driven by the daylight flow. Everything else stays theme-controlled. */
const CSS_VARS: Record<keyof DaylightColors, string[]> = {
  canvas: ['--shh-color-surface-canvas'],
  raised: ['--shh-color-surface-raised', '--shh-color-surface-overlay'],
  sunken: ['--shh-color-surface-sunken'],
  subtle: ['--shh-color-border-subtle'],
  glow: ['--shh-color-ambient-glow'],
};

export interface ApplyOptions {
  /** Theme used at night: 'wall-night' on the wall, 'dark' on phones. */
  nightTheme?: 'dark' | 'wall-night';
}

/** Writes the state onto an element (usually <html>). Returns a function that undoes it. */
export function applyDaylight(
  el: HTMLElement,
  state: DaylightState,
  { nightTheme = 'wall-night' }: ApplyOptions = {},
) {
  el.setAttribute('data-theme', state.world === 'day' ? 'light' : nightTheme);
  el.setAttribute('data-daylight', state.anchor);
  for (const key of KEYS)
    for (const name of CSS_VARS[key]) el.style.setProperty(name, state.colors[key]);
  syncThemeColor(el);
}

export function clearDaylight(el: HTMLElement, previousTheme: string | null) {
  for (const key of KEYS) for (const name of CSS_VARS[key]) el.style.removeProperty(name);
  el.removeAttribute('data-daylight');
  if (previousTheme) el.setAttribute('data-theme', previousTheme);
  else el.removeAttribute('data-theme');
  restoreThemeColor();
}

/**
 * The browser colours the system areas (iOS around the home indicator, Android's navigation bar)
 * with <meta name="theme-color">. index.html picks it by the device's light/dark setting – but the
 * app follows the sun: an iPhone in light mode showed a bright strip under the dark evening app
 * (Mike, 17.10.). So the metas take the page's own background while the daylight rules.
 */
function syncThemeColor(el: HTMLElement) {
  const color = getComputedStyle(el).backgroundColor;
  if (!color) return;
  for (const meta of document.querySelectorAll<HTMLMetaElement>('meta[name="theme-color"]')) {
    if (meta.dataset.content === undefined) {
      meta.dataset.content = meta.content;
      meta.dataset.media = meta.media;
    }
    meta.removeAttribute('media');
    meta.content = color;
  }
}

/** Daylight switched off: the device's light/dark setting decides again (index.html). */
function restoreThemeColor() {
  for (const meta of document.querySelectorAll<HTMLMetaElement>('meta[name="theme-color"]')) {
    if (meta.dataset.content === undefined) continue;
    meta.content = meta.dataset.content;
    if (meta.dataset.media) meta.media = meta.dataset.media;
    delete meta.dataset.content;
    delete meta.dataset.media;
  }
}
